import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Cletya (2026-09-15) — hero on arrival, variant photo on click.
    //
    // The server renders the gallery in natural order (hero / position-1 first) and never
    // reorders it around the selected variant, so landing on the page always shows the hero
    // image (see snippets/product-media-gallery-content.liquid). For a given product the
    // markup returned by a variant update is therefore IDENTICAL to what is already on
    // screen — only the slide to show changes.
    //
    // So for a same-product variant change we do NOT replaceWith() the gallery. Swapping it
    // destroys the initialised <slideshow-component> and any select() we make afterwards is
    // undone by the new component's own start-up (attributeChangedCallback on [initial-slide]
    // -> queueMicrotask, then #setupSlideshow -> rAF, both re-selecting its initial slide).
    // That is exactly why the previous "select() + retries" version never moved the image.
    // Moving the already-initialised slideshow is one call, no race, and keeps the animation.
    const newProduct = event.detail.data.newProduct;

    if (!newProduct) {
      const featuredMediaId = event.detail.resource?.featured_media?.id;

      if (featuredMediaId != null) this.slideshow?.select({ id: String(featuredMediaId) });

      return;
    }

    // Combined listing: a different product was loaded, so the media really changed and the
    // gallery has to be swapped (stock Horizon behaviour).
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
